/**
 * Handles the logic for the product table
 *
 * @param {Object[]} products - The products to display in the table
 * @param {Function} onChange - The function to call when the products change
 * @param {Object[]} sellableItems - The sellable items to display in the table
 * @returns {Object} - The handlers for the product table
 * @property {Function} handleAddProduct - Handles the addition of a product
 * @property {Function} handleAssignProductRank - Handles the assignment of a product rank
 * @property {Function} handleDecrementProductRank - Handles the decrement of a product rank
 * @property {Function} handleIncrementProductRank - Handles the increment of a product rank
 * @property {Function} handleRemoveAllProducts - Handles the removal of all products
 * @property {Function} handleRemoveSelectedProducts - Handles the removal of selected products
 */
export function useProductTableHandlers(products, onChange, sellableItems) {
    const handleAddProduct = (externalSku) => {
        const product = sellableItems.find((p) => p.externalSku === externalSku);
        if (!product) return;
        if (products.some((p) => p.externalSku === product.externalSku)) return;
        onChange((prev) => ({ ...prev, items: [...products, product] }));
    };

    const handleAssignProductRank = (externalSku, targetIndex) => {
        const currentIndex = products.findIndex((p) => p.externalSku === externalSku);
        if (currentIndex === -1 || targetIndex < 0 || targetIndex >= products.length) return;

        const newProducts = [...products];
        const [movedProduct] = newProducts.splice(currentIndex, 1);
        newProducts.splice(targetIndex, 0, movedProduct);

        onChange((prev) => ({ ...prev, items: newProducts }));
    };

    const handleDecrementProductRank = (externalSku) => {
        const productIndex = products.findIndex((p) => p.externalSku === externalSku);
        if (productIndex === -1 || productIndex >= products.length - 1) return;
        handleAssignProductRank(externalSku, productIndex + 1);
    };

    const handleIncrementProductRank = (externalSku) => {
        const productIndex = products.findIndex((p) => p.externalSku === externalSku);
        if (productIndex <= 0) return;
        handleAssignProductRank(externalSku, productIndex - 1);
    };

    const handleRemoveAllProducts = () => {
        onChange((prev) => ({ ...prev, items: [] }));
    };

    const handleRemoveSelectedProducts = (selectedProducts) => {
        const newProducts = products.filter((p) => !selectedProducts.includes(p.externalSku));
        onChange((prev) => ({ ...prev, items: newProducts }));
    };

    return {
        handleAddProduct,
        handleAssignProductRank,
        handleDecrementProductRank,
        handleIncrementProductRank,
        handleRemoveAllProducts,
        handleRemoveSelectedProducts,
    };
}
